import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser, FiMail, FiPhone, FiLock, FiCompass } from "react-icons/fi";
import { FaChevronDown, FaChevronLeft, FaCheck, FaUser, FaPhoneAlt, FaLock, 
  FaBookReader, FaExclamationCircle, FaRegCalendarAlt } from "react-icons/fa";
import { FaCircleCheck, FaLocationDot } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
// import { LuCalendarDays, LuCalendarRange } from "react-icons/lu";
import { TbCalendarTime } from "react-icons/tb";
import { useUser } from "../../context/UserContext";
import { useAnalytics } from "../../context/AnalyticsContext";

const PHONE_COUNTRY_OPTIONS = [
  { value: "US", label: "USA (+1)", dialCode: "+1" },
  { value: "CA", label: "Canada (+1)", dialCode: "+1" },
  { value: "AU", label: "Australia (+61)", dialCode: "+61" },
  { value: "UK", label: "UK (+44)", dialCode: "+44" },
];

const getPasswordStrengthScore = (password) => {
  if (!password) return 0;

  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*()_+\-[\]{};':"\\|,.<>?/-]/.test(password)) score++;

  return score;
};

const getPasswordStrengthText = (score) => {
  if (score === 0) return "";
  if (score === 1) return "Very Weak";
  if (score === 2) return "Weak";
  if (score === 3) return "Moderate";
  if (score === 4) return "Strong";
  return "Very Strong";
};

const getPasswordStrengthClass = (score) => {
  if (score === 0) return "";
  if (score === 1) return "very-weak";
  if (score === 2) return "weak";
  if (score === 3) return "moderate";
  if (score === 4) return "strong";
  return "very-strong";
};

const CustomSelect = ({
  label,
  value,
  options,
  onChange,
  placeholder = "Select",
  icon = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedOption =
    value === "" || value === null || value === undefined
      ? null
      : options.find((opt) => String(opt.value) === String(value));


  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleDocumentPointerDown = useCallback((event) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    if (wrapper.contains(event.target)) return;

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener("pointerdown", handleDocumentPointerDown);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
    };
  }, [isOpen, handleDocumentPointerDown]);

  return (
    <div className="selects-control" ref={wrapperRef}>
      <button
        type="button"
        className={`select-trigger ${isOpen ? "open" : ""}`}
        onClick={toggleOpen}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {icon && <span className="select-icon">{icon}</span>}

        <span className="sort-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <FaChevronDown className={`select-chev ${isOpen ? "rot" : ""}`} />
      </button>

      {isOpen && (
        <div className="select-menu" role="menu">
          {options.map((opt) => {
            const isActive = String(opt.value) === String(value);
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                className={`sort-item ${isActive ? "active" : ""}`}
                onClick={() => handleSelect(opt.value)}
              >
                <span className="item-text">{opt.label}</span>
                {isActive && <FaCheck className="check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
const AccountHolderDetails = ({ onSubmit, onIsHumanChange }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setphoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [gender, setGender] = useState("");
  const [referral, setReferral] = useState("");

  const [errorField, setErrorField] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [errorList, setErrorList] = useState([]);

  const [sliderValue, setSliderValue] = useState(0);
  const [isSliderDisabled, setIsSliderDisabled] = useState(false);
  const [isHuman, setIsHuman] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("Drag to verify you're human.");

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [dob, setDob] = useState("");

  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const [phoneCountry, setPhoneCountry] = useState("US");
  const [phoneDialCode, setPhoneDialCode] = useState("+1");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");


  const navigate = useNavigate();
  const location = useLocation();

  const initialUserType = location.state?.userType || "";
  const [userType, setUserType] = useState(initialUserType);
  const [showUserTypeMessage, setShowUserTypeMessage] = useState(false);

  const { registerWorkerAccount } = useUser();
  const { trackPageHit } = useAnalytics();

  const detectCountry = async () => {
    let detected = "US";

    try {
      const ipRes = await fetch("https://ipapi.co/json/");
      const ipData = await ipRes.json();

      if (ipData.country) {
        detected = ipData.country;
        console.log("🌍 Detected country via IP:", detected);
        return detected;
      }
    } catch (err) {
      console.log("⚠️ IP detection failed:", err);
    }

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (tz.includes("Australia")) detected = "AU";
      else if (tz.includes("London")) detected = "UK";
      else if (tz.includes("America")) detected = "US";

      console.log("⏱️ Detected country via timezone:", detected);
      return detected;
    } catch (err) {
      console.log("⚠️ Timezone detection failed:", err);
    }

    const lang = navigator.language || "en-US";

    if (lang.includes("AU")) detected = "AU";
    else if (lang.includes("GB") || lang.includes("UK")) detected = "UK";
    else if (lang.includes("CA")) detected = "CA";

    console.log("🗣️ Detected country via browser language:", detected);
    return detected;
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      trackPageHit("register");
    }
  }, [trackPageHit]);

  useEffect(() => {
    if (initialUserType) {
      setShowUserTypeMessage(false);
    }
  }, [initialUserType]);


  useEffect(() => {
    if (!isSuccess) return;

    const timeoutId = setTimeout(() => {
      navigate("/login");
    }, 4000);

    return () => clearTimeout(timeoutId);
  }, [isSuccess, navigate]);



  useEffect(() => {
    detectCountry().then((cc) => {
      console.log("✅ Final country selection:", cc);
      const countryCode = cc || "US";
      setPhoneCountry(countryCode);

      const found = PHONE_COUNTRY_OPTIONS.find(
        (opt) => opt.value === countryCode
      );
      if (found) {
        setPhoneDialCode(found.dialCode);
      } else {
        setPhoneDialCode("+1");
      }
    });
  }, []);

  useEffect(() => {
    if (!phoneCountry) return;

    const found = PHONE_COUNTRY_OPTIONS.find(
      (opt) => opt.value === phoneCountry
    );

    console.log(
      "📞 Phone country changed:",
      phoneCountry,
      "dial:",
      found?.dialCode || "(unknown)"
    );
  }, [phoneCountry]);

  const handlePhoneCountryChange = (code) => {
    setPhoneCountry(code);

    const found = PHONE_COUNTRY_OPTIONS.find((opt) => opt.value === code);
    setPhoneDialCode(found?.dialCode || "+1");
  };

  const clearErrorMessage = () => setErrorMessage("");

  const enableSlider = () => {
    setIsSliderDisabled(false);
    setVerifyMessage("Drag to verify you're human.");
  };

  const isEmptyField = () => {
    return (
      !firstName || !lastName || !phoneNumber || !email || !password || !dob
    );
  };

  const validateForm = () => {
    const errors = [];
    const errorsMap = {};

    if (!userType) {
      errors.push({
        field: "userType",
        message: "Account type selection is required.",
      });
      errorsMap.userType = true;
    }

    if (!firstName) {
      errors.push({
        field: "firstName",
        message: "First name is required.",
      });
      errorsMap.firstName = true;
    }

    if (!lastName) {
      errors.push({
        field: "lastName",
        message: "Last name is required.",
      });
      errorsMap.lastName = true;
    }

    const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    if (!email || !emailRegex.test(email)) {
      errors.push({
        field: "email",
        message: "Please provide a valid email address.",
      });
      errorsMap.email = true;
    }

    if (!phoneNumber || phoneNumber.length !== 10 || !/^[\d]+$/.test(phoneNumber)) {
      errors.push({
        field: "phoneNumber",
        message: "A valid 10-digit phone number is required.",
      });
      errorsMap.phoneNumber = true;
    }

    if (!password) {
      errors.push({
        field: "password",
        message: "Please enter a valid password.",
      });
      errorsMap.password = true;
    } else {
      const score = getPasswordStrengthScore(password);
      if (score < 4) {
        errors.push({
          field: "password",
          message:
            "Password too weak. It must include 8+ chars, upper/lowercase, digit & symbol.",
        });
        errorsMap.password = true;
      }
    }

    if (!gender) {
      errors.push({
        field: "gender",
        message: "Gender selection is required.",
      });
      errorsMap.gender = true;
    }

    if (!dob) {
      errors.push({
        field: "dob",
        message: "Please select your full Date Of Birth.",
      });
      errorsMap.dob = true;
    }

    setErrorList(errors.map(e => e.message));

    if (errors.length === 0) {
      setErrorField("");
      setErrorMessage("");
      setFieldErrors({});
      setErrorList([]);
      return true;
    }

    setFieldErrors(errorsMap);

    setErrorField(errors[0].field);


    if (errors.length <= 2) {
      const combined = errors.map((e) => e.message).join(" ");
    } else {
      setErrorMessage(
        "Highlighted fields are required."
      );
    }

    return false;
  };

  const invalidateVerification = () => {
    if (!isHuman && !isSubmitEnabled && sliderValue === 0) return;

    setIsHuman(false);
    setIsSubmitEnabled(false);
    setIsSliderDisabled(false);
    setSliderValue(0);
    setVerifyMessage("Drag to verify you're human.");
  };


  const submitForm = async () => {
    if (isSubmitting) return;

    if (!userType) {
      setErrorList(["Please select account type."]);
      setShowUserTypeMessage(true);
      return;
    }

    if (!validateForm()) return;

    const formData = CollectedFormData();
    formData.userType = userType;

    console.log(
      "🚀 SUBMITTED FORM DATA:",
      JSON.stringify(formData, null, 2)
    );

    try {
      setIsSubmitting(true);

      onSubmit?.(formData);

      const createdUser = await registerWorkerAccount(formData);

      console.log(
        "[AccountHolderDetails] Worker account created successfully:",
        createdUser
      );

      setErrorList([]);
      setErrorMessage("");
      setFieldErrors({});
      setErrorField("");

      setSuccessMessage("Account created successfully");
      setIsSuccess(true);

      setIsSubmitEnabled(false);
    } catch (error) {
      console.error(
        "[AccountHolderDetails] Error creating worker account:",
        error
      );

      setIsSuccess(false);
      setErrorList([
        "We couldn't create your account right now. Please try again in a moment.",
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submitForm();
  };

  const handleDobChange = (part, value) => {
    if (isHuman || isSubmitEnabled) {
      invalidateVerification();
    }
    const newDob = {
      day,
      month,
      year,
      [part]: value,
    };

    setDay(newDob.day);
    setMonth(newDob.month);
    setYear(newDob.year);

    if (newDob.day && newDob.month && newDob.year) {
      setDob(
        `${newDob.year}-${newDob.month.toString().padStart(2, "0")}-${newDob.day
          .toString()
          .padStart(2, "0")}`
      );
    }

    enableSlider();
  };

  const handleSliderChange = (event) => {
    const currentValue = parseInt(event.target.value, 10);
    setSliderValue(currentValue);

    if (currentValue < 80) {
      setIsHuman(false);
      setIsSubmitEnabled(false);
      onIsHumanChange?.(false);
      return;
    }

    if (!validateForm()) {
      setSliderValue(0);
      setIsHuman(false);
      setIsSliderDisabled(true);
      setIsSubmitEnabled(false);
      onIsHumanChange?.(false);
      return;
    }

    setIsHuman(true);
    setIsSubmitEnabled(true);
    setIsSliderDisabled(true);
    setSliderValue(100);
    onIsHumanChange?.(true);
    setVerifyMessage("Drag to verify you're human.");
  };

  const handleClearForm = () => {
    setFirstName("");
    setLastName("");
    setphoneNumber("");
    setGender("");
    setEmail("");
    setPassword("");
    setReferral("");
    setDay("");
    setMonth("");
    setYear("");
    setDob("");
    setSliderValue(0);
    setIsSliderDisabled(false);
    setIsHuman(false);
    setVerifyMessage("Drag to verify you're human.");
    setErrorMessage("");
    setFieldErrors({});
    setErrorList([]);
    setIsSuccess(false);
    setSuccessMessage("");
    setShowUserTypeMessage(false);
    setUserType("");
    onIsHumanChange?.(false);
    setIsSubmitting(false);
  };

  const handleBack = () => {
    navigate("/");
  };

  const passwordScore = getPasswordStrengthScore(password);
  const passwordStrengthText = getPasswordStrengthText(passwordScore);
  const passwordStrengthClass = getPasswordStrengthClass(passwordScore);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const referralOptions = [
    { value: "Google", label: "Google" },
    { value: "Social Media", label: "Social Media" },
    { value: "Radio Advertisement", label: "Radio Advertisement" },
    { value: "News Article", label: "News Article" },
    { value: "Word of Mouth", label: "Word of Mouth" },
    { value: "Event", label: "Event" },
    { value: "Flyer", label: "Flyer" },
    { value: "Jobs Website", label: "Jobs Website" },
    { value: "University or RTO", label: "University or RTO" },
    { value: "Case Manager", label: "Case Manager" },
    { value: "Hospital Staff", label: "Hospital Staff" },
    { value: "Television", label: "Television" },
    { value: "Out of Home Advertising", label: "Out of Home Advertising" },
    { value: "Other", label: "Other" },
  ];

  const genderOptions = [
    { value: "Woman", label: "Woman" },
    { value: "Man", label: "Man" },
    { value: "Transgender Woman", label: "Transgender Woman" },
    { value: "Transgender Man", label: "Transgender Man" },
    { value: "Non-binary", label: "Non-binary" },
    { value: "Genderqueer", label: "Genderqueer" },
    { value: "Agender", label: "Agender" },
    { value: "No Preference", label: "No Preference" },
  ];

  const dayOptions = Array.from({ length: 31 }, (_, idx) => ({
    value: String(idx + 1),
    label: String(idx + 1),
  }));

  const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ].map((name, idx) => ({
    value: String(idx + 1),
    label: name,
  }));

  const dobYearOptions = yearOptions.map((y) => ({
    value: String(y),
    label: String(y),
  }));

  const CollectedFormData = () => {
    const selectedCountry = PHONE_COUNTRY_OPTIONS.find(
      (opt) => opt.value === phoneCountry
    );

    const fullInternationalNumber = `${phoneDialCode}${phoneNumber}`;

    return {
      firstName,
      lastName,
      email,
      // phoneNumber,
      fullInternationalNumber,
      phoneCountry,
      // phoneCountryLabel: selectedCountry?.label,
      password,
      gender,
      referral,
      userType,

      dob: {
        day,
        month,
        year,
        full: dob,
      },
    };
  };



  const selectedPhoneCountry = PHONE_COUNTRY_OPTIONS.find(
    (c) => c.value === phoneCountry
  );

  const phoneFlag = selectedPhoneCountry?.flag || <FaLocationDot />;

  return (
    <div className="account-holder-details-container">
      <div className="back-arrow-header-wrapper">
        <FaChevronLeft className="form-back-arrow" onClick={handleBack} />
        <h3>Create Your Account</h3>
      </div>

      <form onSubmit={handleFormSubmit}>
        <div className="user-type-toggle">
          <div className="user-type-options-wrapper">
            <p className="user-type-label">Account Type</p>
            <div
              className={`user-type-options ${fieldErrors.userType ? "has-error" : ""
                }`}
              role="radiogroup"
              aria-label="Account purpose"
            >
              <button
                type="button"
                role="radio"
                aria-checked={userType === "client"}
                className={`user-type-option ${userType === "client" ? "active" : ""
                  }`}
                onClick={() => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setUserType("client");
                  setShowUserTypeMessage(false);
                  enableSlider();
                }}
              >
                <span className="user-type-title">Hire Helpers</span>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={userType === "worker"}
                className={`user-type-option ${userType === "worker" ? "active" : ""
                  }`}
                onClick={() => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setUserType("worker");
                  setShowUserTypeMessage(false);
                  enableSlider();
                }}
              >
                <span className="user-type-title">Find Jobs</span>
              </button>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className={`field-with-icon ${fieldErrors.firstName ? "has-error" : ""}`}>
              <span className="field-icon">
                <FaUser />
              </span>
              <input
                type="text"
                id="firstname"
                required
                placeholder="First name"
                value={firstName}
                onChange={(e) => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setFirstName(e.target.value);
                  enableSlider();
                }}
                onFocus={clearErrorMessage}
              />
            </div>
          </div>

          <div className="form-column">
            <div className={`field-with-icon ${fieldErrors.lastName ? "has-error" : ""}`}>
              <span className="field-icon">
                <FaUser />
              </span>
              <input
                type="text"
                id="lastname"
                required
                placeholder="Last name"
                value={lastName}
                onChange={(e) => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setLastName(e.target.value);
                  enableSlider();
                }}
                onFocus={clearErrorMessage}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className={`field-with-icon ${fieldErrors.email ? "has-error" : ""}`}>
              <span className="field-icon">
                <MdEmail />
              </span>
              <input
                type="email"
                id="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setEmail(e.target.value);
                  enableSlider();
                }}
                onFocus={clearErrorMessage}
              />
            </div>
          </div>

          <div className="form-column">
            <div className={`field-with-icon ${fieldErrors.password ? "has-error" : ""}`}>
              <span className="field-icon">
                <FaLock />
              </span>
              <input
                type="password"
                id="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  if (isHuman || isSubmitEnabled) {
                    invalidateVerification();
                  }
                  setPassword(e.target.value);
                  enableSlider();
                }}
                onFocus={clearErrorMessage}
              />
            </div>

            <div className="password-strength-wrapper">
              {password && (
                <div
                  className={`password-strength ${passwordStrengthClass}`}
                  id="passwordStrength"
                >
                  <span>Password strength</span>
                  <span> ➤</span>
                  <span>{passwordStrengthText}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className="phone-input-group">
              <div className="phone-country-select">
                <CustomSelect
                  icon={<span className="flag-icon">{phoneFlag}</span>}
                  value={phoneCountry}
                  options={PHONE_COUNTRY_OPTIONS}
                  onChange={(val) => {
                    if (isHuman || isSubmitEnabled) {
                      invalidateVerification();
                    }

                    setPhoneCountry(val);

                    const found = PHONE_COUNTRY_OPTIONS.find(opt => opt.value === val);

                    setPhoneDialCode(found?.dialCode || "+1");

                    enableSlider();
                  }}
                  placeholder="Country"
                />

              </div>

              <div
                className={`field-with-icon ${fieldErrors.phoneNumber ? "has-error" : ""
                  }`}
              >
                <span className="field-icon">
                  <FaPhoneAlt />
                </span>
                <input
                  type="text"
                  id="phoneNumber"
                  required
                  inputMode="numeric"
                  maxLength={10}
                  placeholder={`Phone #`}
                  value={phoneNumber}
                  onChange={(e) => {
                    if (isHuman || isSubmitEnabled) {
                      invalidateVerification();
                    }
                    setphoneNumber(e.target.value.replace(/\D/g, ""));
                    enableSlider();
                  }}
                  onFocus={clearErrorMessage}
                />
              </div>
            </div>
          </div>

          <div className="form-column">
            <CustomSelect
              icon={<FaBookReader />}
              value={referral}
              options={referralOptions}
              onChange={(val) => {
                if (isHuman || isSubmitEnabled) {
                  invalidateVerification();
                }
                setReferral(val);
                enableSlider();
              }}
              placeholder="How did you hear about us?"
            />

          </div>
        </div>

        <div className="form-row form-row-gender-dob">
          <div
            className={`form-column gender-column ${fieldErrors.gender ? "has-error" : ""
              }`}
          >
            <CustomSelect
              icon={<FaUser />}
              value={gender}
              options={genderOptions}
              onChange={(val) => {
                if (isHuman || isSubmitEnabled) {
                  invalidateVerification();
                }
                setGender(val);
                enableSlider();
              }}
              placeholder="Gender"
            />
          </div>

          <div
            className={`dob-container dob-column ${fieldErrors.dob ? "has-error" : ""
              }`}
          >
            <div className="dob-header">
              <span className="dob-title">Date Of Birth</span>
              <span className="register-arrow-indicator">➤</span>
            </div>

            <div className="dob-selects">
              <CustomSelect
                icon={<FaRegCalendarAlt  />}
                value={day}
                options={dayOptions}
                onChange={(val) => handleDobChange("day", val)}
                placeholder="Day"
              />
              <CustomSelect
                icon={<FaRegCalendarAlt  />}
                value={month}
                options={monthOptions}
                onChange={(val) => handleDobChange("month", val)}
                placeholder="Month"
              />
              <CustomSelect
                icon={<FaRegCalendarAlt  />}
                value={year}
                options={dobYearOptions}
                onChange={(val) => handleDobChange("year", val)}
                placeholder="Year"
              />
            </div>
          </div>
        </div>

        <div className="error-message-wrapper">
          {isSuccess ? (
            <div className="register-success-banner">
              <h4 className="human-message">
                {successMessage}
              </h4>
              <FaCircleCheck className="success-icon" />
            </div>
          ) : errorList.length > 0 ? (
            errorList.length === 1 ? (
              <div className="register-error-message">
                <FaExclamationCircle className="single-error-icon" />
                <span>{errorList[0]}</span>
              </div>
            ) : (
              <ul className="register-error-list">
                {errorList.map((msg, idx) => (
                  <li key={idx}>
                    <FaExclamationCircle className="error-icon" />
                    <span>{msg}</span>
                  </li>
                ))}
              </ul>
            )
          ) : isHuman ? (
            <div className="verified-message-icon-wrapper">
              <FaCircleCheck className="verified-icon" />

              <h4 className="human-message">
                Successfully Verified. Click Submit.
              </h4>
            </div>

          ) : (
            <div className="placeholder-block"></div>
          )}
        </div>

        {!isHuman && !isSubmitEnabled && (
          <div className="robot-checkbox-container">
            <div className="slider-container">
              <h4 className="slider-header">{verifyMessage}</h4>
              <input
                className="human-slider"
                type="range"
                min="0"
                max="100"
                value={sliderValue}
                disabled={isSliderDisabled}
                onChange={handleSliderChange}
              />
            </div>
          </div>
        )}


        <div className="back-next-navigation-buttons">
          <button
            type="button"
            className="multiform-clear-button"
            onClick={handleClearForm}
          >
            Clear Fields
          </button>

          {/* <button
  type="submit"
  className={`multiform-next-button ${!isSubmitting ? "is-loading" : ""}`}
  disabled={isSubmitEnabled || !isSubmitting}
>
  {!isSubmitting ? ( */}

          {/* <button
  type="submit"
  className={`multiform-next-button ${isSubmitting ? "is-loading" : ""}`}
  disabled={!isSubmitEnabled || isSubmitting}
>
  {isSubmitting ? ( */}

          <button
            type="submit"
            className={`multiform-next-button ${isSubmitting ? "is-loading" : ""}`}
            disabled={!isSubmitEnabled || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                <span>Sending...</span>
              </>
            ) : (
              "Submit"
            )}
          </button>

        </div>
      </form>
    </div>
  );
};

export default AccountHolderDetails;

